/**
 * Methodology Translations Module (Japanese)
 * Research Approach & Data Sources for JCI Development Councils Youth Futures Observatory
 * @module methodology
 * @language ja
 */

export const methodology = {
  methodology: {
    // Page Meta
    pageTitle: "研究アプローチとデータソース",
    heroSubtitle: "JCI国内組織と地域組織を支援するエビデンスベースのインテリジェンス",
    heroDescription: "JCI開発評議会ユースフューチャーズオブザーバトリーは、JCI組織からの検証済みデータ、グローバルな青少年研究イニシアチブ、確立された経済指標を組み合わせた、厳格で監査可能な方法論に基づいて構築されています。",
    
    // Hero CTAs
    ctaExplore: "方法論を探索",
    ctaContact: "チームに連絡",
    
    // Executive Overview
    executiveOverviewTitle: "エグゼクティブサマリー",
    executiveOverviewText: "この文書は、オブザーバトリーのインサイトを作成するために使用された研究アプローチ、データソース、分析方法を説明しています。表示される各統計は、検証済みの監査可能なソースに直接追跡できます。",
    coreCommitment: "表示される各統計は、検証済みの監査可能なソースに直接追跡できます。",
    
    // Overview Section
    overviewTitle: "DASHCityフレームワーク",
    overviewDescription: "体系的な評価、測定、継続的改善を通じて、組織を高インパクトのパワーハウスに変革するために設計された包括的な5フェーズアプローチ。",
    overviewIntro: "DASHCityは、組織が機会を特定し、パフォーマンスを測定し、価値を伝え、ガバナンスを確立し、グローバルにインパクトを拡大するための構造化された道筋を提供します。",
    frameworkFeatures: "DASHCity 5フェーズフレームワーク",
    inclusiveFeature: "包括的 - あらゆる規模と地域の組織向けに設計",
    scalableFeature: "スケーラブル - ローカルからグローバル実装まで",
    
    // Framework Characteristics
    characteristicsTitle: "フレームワークの原則",
    characteristics: {
      evidenceBased: "エビデンスベース",
      evidenceBasedDesc: "検証されたデータと研究に基づく",
      iterative: "反復的",
      iterativeDesc: "フィードバックループによる継続的改善",
      inclusive: "包括的",
      inclusiveDesc: "あらゆる規模と地域の評議会向けに設計",
      scalable: "スケーラブル",
      scalableDesc: "ローカル組織からグローバル実装まで",
      transparent: "透明",
      transparentDesc: "オープンな方法論とデータ基準",
      actionable: "実行可能",
      actionableDesc: "実践的なツールと明確な成果物"
    },
    
    // Phase Status Badges
    statusDone: "完了",
    statusDoing: "進行中",
    statusToDo: "計画済み",
    
    // Phase Detail Labels
    activitiesLabel: "活動",
    deliverablesLabel: "成果物",
    resultLabel: "結果",
    
    // Section Titles
    dataCollectionTitle: "データ収集方法論",
    
    // Phase 1: IDENTIFY
    phase1Title: "特定",
    phase1Period: "2025年9月〜11月",
    phase1Objective: "青少年リーダーシップ開発エコシステム全体のマッピング",
    phase1Description: "基礎フェーズは、開発評議会の現状を理解し、既存のリソースをマッピングし、成長とインパクトのための主要な機会領域を特定することに焦点を当てます。",
    phase1Activities: "戦略的マッピング、機関診断、ギャップ分析、研究スコープの定義",
    phase1Deliverables: "戦略分析文書、機関診断、データソースインベントリ、研究スコープ文書",
    phase1KeyMetrics: "評議会カバレッジ率、ステークホルダーエンゲージメントレベル、特定された優先領域",
    phase1Result: "研究イニシアチブの明確な戦略的正当化",
    phase1Status: "done",
    
    // Phase 2: MEASURE
    phase2Title: "測定",
    phase2Period: "2025年10月〜12月",
    phase2Objective: "厳格なデータ基準と検証プロセスの確立",
    phase2Description: "測定フェーズは、複数のソースにわたる厳格なデータ収集を実施し、ベースラインメトリクスとパフォーマンス指標を確立します。",
    phase2Activities: "データ品質基準、ソース検証、ベースライン確立、検証プロセスの実装",
    phase2Deliverables: "データ品質基準、ソース検証マトリックス、YFI方法論文書、40以上の監査可能なデータポイント",
    phase2KeyMetrics: "データ完全性率、指標カバレッジ、検証精度",
    phase2Result: "40以上の監査可能なデータポイントの確立",
    phase2Status: "done",
    
    // Phase 3: PROMOTE
    phase3Title: "推進",
    phase3Period: "2025年12月〜2026年1月",
    phase3Objective: "価値の伝達とステークホルダーの関与",
    phase3Description: "推進フェーズは、測定インサイトをオブザーバトリーの価値とインパクトを伝える説得力のあるナラティブに変換します。",
    phase3Activities: "戦略的価値ナラティブ、コミュニケーション資料、エンゲージメントチャネル、ステークホルダーアウトリーチ",
    phase3Deliverables: "多言語資料、ステークホルダー別価値提案、エンゲージメントチャネル、貢献ガイドライン",
    phase3KeyMetrics: "ステークホルダーリーチ、エンゲージメント率、メッセージ共鳴スコア",
    phase3Result: "多言語コミュニケーション資料の運用開始",
    phase3Status: "doing",
    
    // Phase 4: DIRECT
    phase4Title: "指揮",
    phase4Period: "2026年1月〜2月",
    phase4Objective: "ガバナンスと品質監督の確立",
    phase4Description: "指揮フェーズは、堅牢なガバナンスフレームワークを確立し、役割と責任を定義し、持続可能な運営構造を作成します。",
    phase4Activities: "ガバナンス構造、検証プロセス、更新サイクル、責任定義",
    phase4Deliverables: "ガバナンス構造、責任定義、品質チェックリスト、更新サイクルスケジュール",
    phase4KeyMetrics: "ガバナンス採用率、意思決定効率、コンプライアンスレベル",
    phase4Result: "4つの開発評議会によるガバナンス構造",
    phase4Status: "doing",
    
    // Phase 5: SCALE
    phase5Title: "拡大",
    phase5Period: "2026年1月〜2027年12月以降",
    phase5Objective: "リーチ、インパクト、グローバルな影響力の拡大",
    phase5Description: "拡大フェーズは、方法論のリーチを拡大し、ネットワーク効果を通じてインパクトを増幅し、フレームワークをグローバルリファレンスとして確立することに焦点を当てます。",
    phase5Activities: "15以上のNOへの拡大、パブリックAPI、学術パートナーシップ、政策への影響",
    phase5Deliverables: "グローバル展開計画、パブリックAPI、学術パートナーシップ、政策影響文書",
    phase5KeyMetrics: "地理的カバレッジ、登録ユーザー数、メディア引用",
    phase5Result: "グローバル権威としてのプラットフォームスケーリング",
    phase5Status: "todo",
    
    // Data Sources Section
    dataSourcesTitle: "データソース",
    dataSourcesDescription: "JCIおよびグローバル研究の検証済みソースからの包括的なデータ",
    primaryDataSourcesTitle: "プライマリデータソース",
    jciInstitutionalData: "JCI機関データ",
    yfiGlobalData: "ユースフューチャーズインデックスとグローバルデータ",
    secondarySourcesTitle: "セカンダリデータソース",
    
    // Table Column Headers
    sourceColumn: "ソース",
    coverageColumn: "カバレッジ",
    categoryColumn: "カテゴリ",
    dimensionColumn: "次元",
    weightColumn: "重み",
    sourcesColumn: "ソース",
    criterionColumn: "基準",
    exampleColumn: "例",
    gapColumn: "ギャップ",
    mitigationColumn: "緩和策",
    
    // Primary Data Source Items
    sourceJCIImpactReport: "JCIインパクトレポート2025",
    sourceJCIVision2040: "JCIビジョン2040戦略計画",
    sourceJCIMembershipSurvey: "JCIメンバーシップ調査2025",
    coverageImpactReport: "148,122名のメンバー、4,641のLO",
    coverageVision2040: "2040年までの戦略目標",
    coverageSurvey: "3,790名の回答者",
    categoryJciInstitutional: "JCI機関",

    // YFI Dimensions Table
    yfiDimensionsTitle: "ユースフューチャーズインデックスの次元",
    dimensionMentalHealth: "メンタルヘルス",
    dimensionWorkforce: "労働力と教育",
    dimensionEntrepreneurship: "起業家精神",
    dimensionEducation: "教育",
    coverageCountries: "114か国",
    sourceMentalHealth: "WHO、Sapien Labs MHQ",
    sourceWorkforce: "ILO、UNESCO、世界銀行",
    sourceEntrepreneurship: "世界銀行、GEM、ITU",
    
    // Formula Legend
    formulaLabel: "ユースフューチャーズインデックス計算式",
    formulaLegendMH: "MH = メンタルヘルス",
    formulaLegendWF: "WF = 労働力と教育",
    formulaLegendENT: "ENT = 起業家精神",
    
    // DC Partnership
    dcPartnershipDescription: "開発評議会がオブザーバトリーとどのように協力するか",
    dcProvidedTitle: "DCが提供したもの",
    dcNotProvidedTitle: "DCが提供しなかったもの",
    dcProvidedStrategic: "戦略的コラボレーション、地域フィードバック",
    dcProvidedRegional: "全地域での普及サポート",
    dcProvidedValidation: "発見に関する地域コンテキスト、関連性の確認",
    dcProvidedPlansOfAction: "2025年行動計画、地域戦略",
    dcNotProvidedPrimaryData: "一次研究データ",
    dcNotProvidedSurveyDesign: "調査設計または実施",
    dcNotProvidedBaseline: "ベースラインデータ収集",
    dcNotProvidedAnalysis: "データ分析",
    
    // Data Collection Section
    dataCollectionDescription: "データ収集と分析の5つの重要領域",
    dataCollectionJCI: "JCIデータ統合",
    dataCollectionJCIDesc: "公式JCIインパクトレポート2025を収集、メンバーデータを抽出、地域別に整理",
    dataCollectionSurvey: "調査データ分析",
    dataCollectionSurveyDesc: "人口統計、動機、スキルギャップのために3,790名の回答者を分析",
    dataCollectionVision: "ビジョン2040ドキュメント",
    dataCollectionVisionDesc: "成長目標、戦略的柱を抽出、シナリオプランニングを開発",
    dataCollectionYFI: "YFI統合",
    dataCollectionYFIDesc: "114のJCI国をYFIランキングにマッピング、地域スコアを集約",
    dataCollectionMentalHealth: "メンタルヘルス詳細分析",
    dataCollectionMentalHealthDesc: "WHOデータを統合、Sapien Labs研究を分析、JCI RISEに接続",
    
    // Verification Checklist Items
    verifySourceIdentifiable: "ソース識別可能",
    verifyDateConfirmed: "日付確認済み",
    verifyMethodologyDocumented: "方法論文書化済み",
    verifySampleSizeStated: "サンプルサイズ記載済み",
    verifyLimitationsAcknowledged: "制限事項認識済み",
    verifyCalculationsReproducible: "計算再現可能",
    
    // Zero Synthetic Data
    zeroSyntheticDescription: "オブザーバトリーには合成データは含まれていません",
    zeroSyntheticItem1: "現在の事実として提示される予測なし",
    zeroSyntheticItem2: "モデル生成統計なし",
    zeroSyntheticItem3: "補間値なし",
    zeroSyntheticItem4: "推定値なし（明示的にマークされている場合を除く）",
    zeroSyntheticItem5: "仮定された分布なし",
    
    // Acknowledged Limitations
    limitationSurveyBias: "調査回答バイアス",
    mitigationSurveyBias: "地域的重み付け適用、バイアス文書化",
    limitationCoverage: "YFI国カバレッジギャップ",
    mitigationCoverage: "地域平均計算済み、ギャップ記載",
    limitationLagging: "遅行指標",
    mitigationLagging: "最新データ使用（2024-2025）",
    limitationLanguage: "言語制限",
    mitigationLanguage: "5言語での翻訳、英語バイアス記載",
    
    // Vision 2040 Alignment
    vision2040AlignmentDescription: "オブザーバトリーがJCIの戦略的ビジョンをどのようにサポートするか",
    strategyWhereExpand: "どこに拡大するか",
    strategyWhereExpandDesc: "YFIスコアが55未満のアフリカ・中東地域での優先拡大",
    strategyWhatPrioritize: "何を優先するか",
    strategyWhatPrioritizeDesc: "メンタルヘルス次元（40%の重み）を主要フォーカスエリアとして",
    strategyHowScale: "どのようにスケールするか",
    strategyHowScaleDesc: "技術採用目標：80%以上のデジタルエンゲージメント",
    strategySuccessMeasures: "成功指標",
    strategySuccessMeasuresDesc: "目標：2030年までに地域YFIスコアを4-7ポイント改善",
    
    // Transparency Section
    transparencyDescription: "完全な方法論の透明性と再現性",
    reproducibilityTitle: "再現性",
    reproducibilityDescription: "どの研究者も公開されたソースと方法論を使用してこの分析を再現できます",
    noProprietaryTitle: "独自の方法なし",
    noProprietaryDescription: "ブラックボックス計算なし、すべての計算式と重みが公開されています",
    
    // Summary Section
    summaryDescription: "研究方法論実装の現在のステータス",
    summaryCompletedItem1: "フェーズ1：特定",
    summaryCompletedItem2: "フェーズ2：測定",
    summaryCompletedItem3: "データアーキテクチャ",
    summaryCompletedItem4: "YFI計算",
    summaryCompletedItem5: "114か国のカバレッジ",
    summaryCompletedItem6: "多言語プラットフォーム",
    summaryCompletedItem7: "DCパートナーシップモデル",
    summaryInProgressItem1: "フェーズ3：推進",
    summaryInProgressItem2: "フェーズ4：指揮",
    summaryInProgressItem3: "ユーザーフィードバック統合",
    summaryInProgressItem4: "地域詳細分析",
    summaryPlannedItem1: "フェーズ5：拡大",
    summaryPlannedItem2: "UN/WEF統合",
    summaryPlannedItem3: "API開発",
    
    // Data Integrity Section
    dataIntegrityTitle: "データ整合性基準",
    verificationChecklistTitle: "検証チェックリスト",
    zeroSyntheticTitle: "合成データゼロへのコミットメント",
    zeroSyntheticException: "例外：ビジョン2040のシナリオ予測は「シナリオ」として明確にラベル付けされ、予測ではなく例示的なものです。",
    acknowledgedLimitationsTitle: "認識されている制限",
    
    // Verification Checklist Examples
    verifyExampleSource: "JCI Impact Report 2025, p.12",
    verifyExampleDate: "2024年12月時点のデータ",
    verifyExampleMethodology: "付録に調査方法論を記載",
    verifyExampleSampleSize: "N = 3,790名の回答者",
    verifyExampleLimitations: "英語バイアスを認識",
    verifyExampleCalculations: "YFI計算式を公開",
    
    // DC Partnership Section
    dcPartnershipTitle: "開発評議会パートナーシップ",
    partnershipModelTitle: "パートナーシップモデル",
    whatDCsProvided: "DCが提供したもの",
    whatDCsDidNotProvide: "DCが提供しなかったもの",
    
    // Measurement Modules
    modulesTitle: "測定モジュール",
    modulesDescription: "4つのテーマ領域が開発評議会のパフォーマンスとインパクトの包括的評価を保証します。",
    
    module1Title: "戦略的整合性",
    module1Description: "JCIのミッション、ビジョン、グローバル戦略優先事項との整合性を測定",
    module1Indicators: "ビジョンの明確さ、ミッション採用、戦略計画の質、グローバル整合性スコア",
    
    module2Title: "運営の卓越性",
    module2Description: "運営効率、リソース管理、組織の健全性を評価",
    module2Indicators: "リソース利用率、プロセス効率、メンバー満足度、財務的持続可能性",
    
    module3Title: "教育品質",
    module3Description: "トレーニングプログラム、スキル開発、知識移転の効果を評価",
    module3Indicators: "プログラム品質スコア、学習成果、認定率、トレーナーの効果",
    
    module4Title: "インパクトと変革",
    module4Description: "具体的な成果、コミュニティインパクト、変革的変化を測定",
    module4Indicators: "プロジェクト完了率、受益者リーチ、SDG整合性、持続可能性メトリクス",
    
    // Areas of Opportunity
    areasTitle: "機会の領域",
    areasDescription: "方法論は、開発評議会がインパクトを最大化できる4つの相互接続された領域に対応します。",
    
    areaIndividual: "個人の成長",
    areaIndividualFocus: "メンバーの個人的成長とリーダーシップ開発",
    areaIndividualMetrics: "獲得したスキル、リーダーシップポジション、キャリア向上",
    
    areaCommunity: "コミュニティインパクト",
    areaCommunityFocus: "プロジェクトとイニシアチブを通じた地域コミュニティでのポジティブな変化",
    areaCommunityMetrics: "完了したプロジェクト、到達した受益者、持続可能な成果",
    
    areaInternational: "国際的つながり",
    areaInternationalFocus: "グローバルエンゲージメントと異文化間コラボレーション",
    areaInternationalMetrics: "国際イベント、形成されたパートナーシップ、交流参加",
    
    areaEntrepreneurial: "起業家精神",
    areaEntrepreneurialFocus: "イノベーション、ビジネス開発、経済的機会創出",
    areaEntrepreneurialMetrics: "支援したビジネス、イノベーションプログラム、経済的インパクト",
    
    // Data Standards
    dataStandardsTitle: "データ基準",
    dataStandardsDescription: "厳格な基準がすべてのソースでデータの品質、信頼性、比較可能性を保証します。",
    
    acceptedTitle: "受け入れられるデータ基準",
    acceptedVerifiable: "公式ソースを通じて検証可能",
    acceptedAuditable: "明確な文書化の軌跡で監査可能",
    acceptedTraceable: "元の収集ポイントまで追跡可能",
    acceptedAccurate: "定義された許容レベル内で正確",
    acceptedCurrent: "最新（指定された期間内に収集）",
    acceptedContextualized: "関連するメタデータでコンテキスト化",
    acceptedCited: "適切に引用・帰属",
    
    notAcceptedTitle: "受け入れ不可",
    notAcceptedSynthetic: "検証なしの合成またはAI生成データ",
    notAcceptedUnverifiable: "検証不能な主張または推定",
    notAcceptedProprietary: "許可のない独自データ",
    notAcceptedIncomplete: "主要変数が欠落した不完全なデータセット",
    notAcceptedBiased: "母集団を代表しないバイアスのかかったサンプル",
    
    // Validation Process
    validationTitle: "検証プロセス",
    validationDescription: "厳格な5ステップ検証がデータの整合性と信頼性を保証します。",
    
    validationStep1: "初期収集",
    validationStep1Desc: "標準化されたツールとプロトコルを使用してデータを収集",
    validationStep2: "品質チェック",
    validationStep2Desc: "自動および手動の品質保証手順",
    validationStep3: "クロス検証",
    validationStep3Desc: "複数のデータソースに対する検証",
    validationStep4: "専門家レビュー",
    validationStep4Desc: "分野専門家による評価と承認",
    validationStep5: "最終認証",
    validationStep5Desc: "公式認証と公開承認",
    
    // Governance Structure
    governanceTitle: "ガバナンス構造提案",
    governanceDescription: "明確なガバナンスが説明責任、透明性、効果的な意思決定を保証します。",
    
    governanceLevel1: "JCI国際理事会",
    governanceLevel1Desc: "戦略的監督とポリシー方向性",
    governanceLevel2: "開発評議会",
    governanceLevel2Desc: "データ検証と地域調整",
    governanceLevel3: "エグゼクティブチーム",
    governanceLevel3Desc: "運営と実装",
    governanceLevel4: "コントリビューターとユーザー",
    governanceLevel4Desc: "コミュニティフィードバックとエンゲージメント",
    
    // Update Cycles
    updateCyclesTitle: "更新サイクル",
    updateCycleAnnual: "年次",
    updateCycleAnnualDesc: "完全なデータ更新（11月収集、12月検証、1月公開）",
    updateCycleIntermediate: "中間",
    updateCycleIntermediateDesc: "重要な新しい研究またはプログラムイノベーション",
    updateCycleContinuous: "継続的",
    updateCycleContinuousDesc: "エラー修正、リンク更新、プラットフォーム改善",
    
    // Vision 2040 Alignment
    vision2040AlignmentTitle: "JCIビジョン2040との整合",
    strategicIntelligenceTitle: "戦略的インテリジェンス機能",
    strategicRecommendationsTitle: "戦略的推奨事項",
    
    // Transparency Section
    transparencyTitle: "透明性と再現性",
    completeSourceInventoryTitle: "完全なソースインベントリ",
    jciDocuments: "JCI公式文書",
    globalResearchSources: "グローバル研究ソース",
    formulaReproducibility: "どの研究者もこの分析を再現できます",
    formulaExplanation: "FII = (0.40 × MH) + (0.30 × WF) + (0.30 × ENT)",
    fullTransparency: "独自の方法論なし、ブラックボックス計算なし、隠れた仮定なし。",
    
    // Summary Section
    summaryTitle: "研究方法論のステータス",
    summaryCompleted: "完了",
    summaryInProgress: "進行中",
    summaryPlanned: "計画済み",
    
    // Expansion Roadmap
    roadmapTitle: "拡張ロードマップ",
    roadmapDescription: "グローバル採用と継続的改善のためのフェーズアプローチ。",
    
    roadmap2025: "2025年：基盤",
    roadmap2025Desc: "4つの開発評議会とのパイロット実装、データ基準の確立",
    roadmap2026: "2026年：拡大（ベータ）",
    roadmap2026Desc: "15以上のNOが貢献、200人以上のユーザー、研究者向けパブリックAPI",
    roadmap2027: "2027年以降：グローバル権威",
    roadmap2027Desc: "UN/WEF/UNESCO統合、1,000人以上のユーザー、10カ国以上での政策影響",
    
    // Success Factors
    successTitle: "成功要因",
    successDescription: "方法論の成功した実装に貢献する主要な要素。",
    
    successFactor1: "強力なエグゼクティブスポンサーシップとコミットメント",
    successFactor2: "明確なコミュニケーションと変革管理",
    successFactor3: "十分なリソースと専任チーム",
    successFactor4: "継続的なフィードバックによる反復的アプローチ",
    successFactor5: "テクノロジー活用と自動化",
    successFactor6: "文化的感受性と地域適応",
    
    // Lessons Learned
    lessonsTitle: "学んだ教訓",
    lessonsDescription: "パイロット実装とステークホルダーフィードバックからのインサイト。",
    
    lesson1: "モメンタムを構築するために意欲的な早期採用者から始める",
    lesson2: "トレーニングと能力開発に大きく投資する",
    lesson3: "標準化とローカルの柔軟性のバランスをとる",
    lesson4: "エンゲージメントを維持するためにクイックウィンを祝う",
    lesson5: "最初からフィードバックメカニズムを組み込む",
    
    // FAQ Section
    faqTitle: "方法論FAQ",
    faqDescription: "研究方法論に関するよくある質問。",
    
    faqQ1: "オブザーバトリーはどのデータソースを使用していますか？",
    faqA1: "オブザーバトリーは3つの主要ソースを組み合わせています：JCI機関データ（インパクトレポート、メンバーシップ調査、ビジョン2040）、114カ国をカバーするユースフューチャーズインデックス、WHO、ILO、UNESCO、世界銀行などの確立されたソースからのグローバル研究。",
    
    faqQ2: "データ品質はどのように保証されますか？",
    faqA2: "すべてのデータは7基準の検証チェックリストを通過する必要があります：検証可能、監査可能、追跡可能、正確、最新、コンテキスト化、適切に引用。5ステップの検証プロセスが公開前に整合性を保証します。",
    
    faqQ3: "「ゼロ合成データ」とは何を意味しますか？",
    faqA3: "予測、モデル生成統計、補間値、または推定値が事実として提示されることはありません。表示されるすべてのメトリクスは検証済みソースに直接追跡できます。唯一の例外はビジョン2040シナリオで、これらは説明目的として明確にラベル付けされています。",
    
    faqQ4: "データはどのくらいの頻度で更新されますか？",
    faqA4: "オブザーバトリーは3つの更新サイクルに従います：年次完全更新（11月〜1月）、重要な新しい研究のための中間更新、修正とプラットフォーム更新のための継続的改善。",
    
    faqQ5: "研究者は分析を再現できますか？",
    faqA5: "はい。すべてのソース文書は公開されており、集計ロジックは文書化されており、FII式（0.40×MH + 0.30×WF + 0.30×ENT）は透明です。どの研究者も結果を再現し検証できます。",
    
    // CTA Section
    ctaTitle: "オブザーバトリーを探索する準備はできていますか？",
    ctaDescription: "組織の戦略的決定を支援するエビデンスベースのインサイトにアクセスしてください。",
    ctaButton1: "ダッシュボードを探索",
    ctaButton2: "貢献方法",
    ctaButton3: "FAQを読む",
    
    // Source Attribution
    sourceTitle: "ソース",
    sourceDescription: "方法論バージョン3.1、2026年1月",
    sourceNote: "グローバルステークホルダーと協力してJCI開発評議会により開発"
  }
} as const;
